import useSafeState from "../utils/safe-state";
import {Button, Col, DatePicker, Form, Row, Select, Spin} from "antd";
import React, {useEffect} from "react";
import {Trans} from "@lingui/macro";
import {useAxios} from "../utils/hooks";
import {openNotification} from "../utils/openNotification";
import moment from "moment";

interface Interface{
    data?: any;
    mode?: 'Edit' | 'Add new';
    onClose?: () => void;
}

const LegacyStockDrawer = (props: Interface) => {

    const axiosInstance = useAxios(process.env.REACT_APP_API_BASE_URL + "");
    const config = {headers: {'Content-Type': 'application/json'}};
    const data = props.data || {};
    const [form] = Form.useForm();
    const [loading, setLoading] = useSafeState(false);

    const [itemOptions, setItemOptions] = useSafeState<any[]>([]);
    const [itemLoading, setItemLoading] = useSafeState(false);

    useEffect(() => {
        form.resetFields();
        getItems();

        if(props.mode === 'Edit' && data && data.id) {
            let fieldsValues = [];
            for (const [key, value] of Object.entries(data)) {
                if(key === 'validTo') {
                    fieldsValues.push({name: key, value: value ? moment(value as string) : null});
                } else if(key === 'item' && value && typeof value === 'object') {
                    fieldsValues.push({name: ['item', 'id'], value: (value as any).id});
                } else {
                    fieldsValues.push({name: key, value: value});
                }
            }
            form.setFields(fieldsValues);
        }
    }, [props.mode]);

    const getItems = () => {
        if (axiosInstance.current != null) {
            setItemLoading(true);
            axiosInstance.current
                .get('/resource/items')
                .then(response => {
                    const items = Array.isArray(response.data) ? response.data : [];
                    setItemOptions(items);
                    console.log('Items loaded:', items.length);
                })
                .catch(function (error: any) {
                    console.error('Error loading items:', error);
                    openNotification('error', 'Failed to load items');
                })
                .finally(() => {
                    setItemLoading(false);
                });
        } else {
            console.warn('Axios instance is null');
        }
    };

    const handleEdit = (values: any) => {
        if (axiosInstance.current != null) {
            setLoading(true);
            axiosInstance.current
                .put(
                    '/resource/legacy-stock/' + encodeURIComponent(data.id),
                    {
                        id: data.id,
                        item: {
                            id: (values.item && values.item.id) || values.item
                        },
                        validTo: values.validTo ? moment(values.validTo).format('YYYY-MM-DD HH:mm') : null,
                        version: form.getFieldValue(['version'])
                    },
                    config,
                )
                .then(() => {
                    openNotification('success', 'Legacy Stock successfully updated.');
                    onReset();
                    if (props.onClose) {
                        props.onClose();
                    }
                })
                .catch(function (error: any) {
                    openNotification('error', error, props);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    };

    const onReset = () => {
        form.resetFields();
    };

    const handleFinish = (values: any) => {
        if (axiosInstance.current != null) {
            setLoading(true);
            axiosInstance.current
                .post(
                    '/resource/legacy-stock',
                    {
                        item: {
                            id: (values.item && values.item.id) || values.item
                        },
                        validTo: values.validTo ? moment(values.validTo).format('YYYY-MM-DD HH:mm') : null
                    },
                    config,
                )
                .then(() => {
                    openNotification('success', 'Legacy Stock successfully created.');
                    onReset();
                    if (props.onClose) {
                        props.onClose();
                    }
                })
                .catch(function (error: any) {
                    openNotification('error', error, props);
                })
                .finally(() => {
                    setLoading(false);
                });
        }
    }

    return (
        <Form
            id="newLegacyStock"
            layout='vertical'
            form={form}
            onFinish={(values) => (props.mode === 'Edit' ? handleEdit(values) : handleFinish(values))}
        >
            <Spin spinning={loading || itemLoading}>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            name={['item', 'id']}
                            label={<Trans>Item</Trans>}
                            rules={[{ required: true, message: <Trans>Please select an item</Trans> }]}
                        >
                            <Select
                                showSearch
                                allowClear
                                placeholder={<Trans>Select item</Trans>}
                                optionFilterProp="children"
                                disabled={props.mode === 'Edit'}
                            >
                                {itemOptions.map(item => (
                                    <Select.Option key={item.id} value={item.id}>
                                        {item.itemNo} - {item.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item
                            name="validTo"
                            label={<Trans>Valid To</Trans>}
                            rules={[{ required: true, message: <Trans>Please select a valid to date</Trans> }]}
                        >
                            <DatePicker style={{ width: '100%' }} showTime format="YYYY-MM-DD HH:mm" />
                        </Form.Item>
                    </Col>
                </Row>
            </Spin>

            <div>
                {props.mode === 'Edit'
                    ? (<Button htmlType="submit" type="primary">
                        <Trans>Save</Trans>
                    </Button>)
                    :
                    <>
                        <Button htmlType="button" style={{ marginRight: 8 }} onClick={() => onReset()}>
                            <Trans>Clear</Trans>
                        </Button>
                        <Button
                            loading={loading}
                            htmlType="submit"
                            type="primary"
                            style={{ marginRight: 8 }}
                        >
                            <Trans>Save</Trans>
                        </Button>
                    </>
                }
            </div>
        </Form>
    );

}
export default LegacyStockDrawer;

